
import React from 'react';
import { X, Volume2, Type } from 'lucide-react';
import { Puzzle, AppSettings } from '../types';

interface StoryModalProps {
  puzzle: Puzzle;
  isOpen: boolean;
  onClose: () => void;
  settings: AppSettings;
  onUpdateSettings: (s: Partial<AppSettings>) => void;
  onListen: () => void;
}

const StoryModal: React.FC<StoryModalProps> = ({ puzzle, isOpen, onClose, settings, onUpdateSettings, onListen }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[400] flex items-center justify-center p-4 pt-safe pb-safe px-safe">
      <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-xl" onClick={onClose}></div>
      <div className="relative bg-white w-full max-w-2xl max-h-[90vh] rounded-[3rem] shadow-2xl animate-in zoom-in duration-300 flex flex-col overflow-hidden">
        <header className="p-6 border-b border-gray-100 flex items-center justify-between shrink-0 bg-white z-10">
          <div className="flex items-center gap-3">
            <div className="bg-blue-50 p-3 rounded-2xl">
              <Type className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h2 className="font-cinzel font-bold text-xl text-gray-900">{puzzle.storyTitle || puzzle.title}</h2>
              <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">Story Passage</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button 
              onClick={onListen}
              className="p-3 bg-blue-600 text-white rounded-2xl hover:bg-blue-700 transition-all active:scale-95 shadow-lg shadow-blue-200"
              title="Listen to Story"
            >
              <Volume2 className="w-5 h-5" />
            </button>
            <button onClick={onClose} className="p-3 text-gray-300 hover:text-gray-500 transition-colors">
              <X className="w-6 h-6" />
            </button>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto p-8 md:p-12 scroll-smooth">
          <div 
            className="prose prose-slate max-w-none text-gray-700 leading-relaxed font-serif"
            style={{ fontSize: `${settings.textSize}px` }}
          >
            {puzzle.storyPassage ? (
              <div className="whitespace-pre-wrap">
                {puzzle.storyPassage}
              </div>
            ) : (
              <div className="italic text-gray-400 text-center py-20">
                No story passage available for this puzzle yet.
              </div>
            )}
            
            {puzzle.storyPassage && (
              <div className="mt-12 pt-8 border-t border-gray-100">
                <p className="text-[10px] text-gray-400 italic">
                  Scripture taken from the Berean Standard Bible (BSB) © 2016-2024 by Bible Hub and Berean.Bible. Used by permission. All rights reserved.
                </p>
              </div>
            )}
          </div>
        </div>

        <footer className="p-6 bg-gray-50 border-t border-gray-100 shrink-0 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Text Size</span>
            <div className="flex items-center gap-1 bg-white p-1 rounded-xl border border-gray-200">
              {[14, 16, 18, 20, 24].map((size) => (
                <button
                  key={size}
                  onClick={() => onUpdateSettings({ textSize: size })}
                  className={`w-8 h-8 rounded-lg font-bold text-xs transition-all ${
                    settings.textSize === size 
                    ? 'bg-blue-600 text-white shadow-md' 
                    : 'text-gray-400 hover:bg-gray-50'
                  }`}
                >
                  {size}
                </button>
              ))}
            </div>
          </div>
          <button 
            onClick={onClose}
            className="bg-gray-900 text-white px-8 py-3 rounded-2xl font-bold text-sm hover:bg-gray-800 transition active:scale-95"
          >
            Close
          </button>
        </footer>
      </div>
    </div>
  );
};

export default StoryModal;
